import { useEffect, useState } from "react";
import {
  listAdjustmentFactors,
  createAdjustmentFactor,
  deleteAdjustmentFactor,
  type AdjustmentFactor,
} from "../../api/client";

interface Props {
  symbol: string;
}

export default function AdjustmentFactorsPanel({ symbol }: Props) {
  const [factors, setFactors] = useState<AdjustmentFactor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [effectiveDate, setEffectiveDate] = useState("");
  const [factor, setFactor] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listAdjustmentFactors(symbol)
      .then((res) => setFactors(res.data))
      .catch((err) => setError(err?.response?.data?.detail ?? err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const handleAdd = async () => {
    setFormError(null);
    const f = parseFloat(factor);
    if (!effectiveDate) { setFormError("Effective date is required."); return; }
    if (isNaN(f) || f <= 0) { setFormError("Factor must be a positive number."); return; }

    setSaving(true);
    try {
      await createAdjustmentFactor({
        symbol,
        effective_date: effectiveDate,
        factor: f,
        note: note.trim() || null,
      });
      setEffectiveDate("");
      setFactor("");
      setNote("");
      load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setFormError(e?.response?.data?.detail ?? e?.message ?? "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this adjustment factor?")) return;
    try {
      await deleteAdjustmentFactor(id);
      load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? "Delete failed.");
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 20,
        marginTop: 20,
      }}
    >
      <h3 style={{ margin: "0 0 4px", color: "#1e293b", fontSize: 15 }}>
        Adjustment Factors — {symbol}
      </h3>
      <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 13 }}>
        Multiply prices strictly before&nbsp;<em>Effective Date</em> by&nbsp;<em>Factor</em> to
        produce a backward-adjusted time series (e.g.{" "}
        <strong>0.5</strong> for a 2-for-1 split).
      </p>

      {error && (
        <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>
      )}

      {/* Existing factors table */}
      {loading ? (
        <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</div>
      ) : factors.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
          No adjustment factors for {symbol}.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["Effective Date", "Factor", "Note", ""].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "6px 10px",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {factors.map((f) => (
              <tr key={f.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>{f.effective_date}</td>
                <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>
                  {Number(f.factor).toPrecision(8).replace(/\.?0+$/, "")}
                </td>
                <td style={{ padding: "6px 10px", color: "#64748b" }}>{f.note ?? "—"}</td>
                <td style={{ padding: "6px 4px", textAlign: "right" }}>
                  <button
                    onClick={() => handleDelete(f.id)}
                    style={{
                      background: "none",
                      border: "1px solid #fca5a5",
                      color: "#dc2626",
                      borderRadius: 4,
                      padding: "2px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add form */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "flex-end",
          borderTop: "1px solid #e2e8f0",
          paddingTop: 14,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#475569" }}>
          Effective Date *
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#475569" }}>
          Factor *
          <input
            type="number"
            step="any"
            min="0.000001"
            placeholder="e.g. 0.5"
            value={factor}
            onChange={(e) => setFactor(e.target.value)}
            style={{ ...inputStyle, width: 110 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#475569" }}>
          Note
          <input
            type="text"
            placeholder="e.g. 2-for-1 split"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ ...inputStyle, width: 180 }}
          />
        </label>
        <button
          onClick={handleAdd}
          disabled={saving}
          style={{
            padding: "6px 14px",
            background: "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: saving ? "not-allowed" : "pointer",
            fontSize: 13,
            fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Add Factor"}
        </button>
      </div>
      {formError && (
        <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{formError}</div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "5px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 13,
  color: "#1e293b",
  background: "#fff",
};
